import { FC, useContext } from 'react';

import { layoutContext } from '@context/layout';

import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Img } from 'react-image';

import styles from './WorkspaceButton.module.scss';
import { AnimatePresence, motion } from 'motion/react';

interface WorkspaceImageProps {
  url: string | null | undefined;
  placeholderText: string;
}

// An SVG is used as it allows the text size to be proportional to the size of the container
const PlaceholderImage: FC<React.PropsWithChildren<{ placeholderText: string }>> = ({ placeholderText }) => (
  <svg className={styles['placeholder-image']} viewBox="0 0 16 16">
    <text x="50%" y="60%" className={styles['placeholder-text']}>
      {placeholderText}
    </text>
  </svg>
);

const WorkspaceImage: FC<React.PropsWithChildren<WorkspaceImageProps>> = ({ url, placeholderText }) => {
  return (
    <div className={styles['workspace-image']}>
      {!url && <PlaceholderImage placeholderText={placeholderText} />}
      {url && <Img src={url} unloader={<PlaceholderImage placeholderText={placeholderText} />} />}
    </div>
  );
};

interface WorkspaceButtonProps {
  onClick: () => void;
  setButtonNode: (instance: HTMLButtonElement | null) => void;
  workspaceName: string;
  workspaceImage: string | null;
}

const WorkspaceButton: FC<React.PropsWithChildren<WorkspaceButtonProps>> = ({
  onClick,
  setButtonNode,
  workspaceName,
  workspaceImage,
}) => {
  const { state: navState } = useContext(layoutContext);

  return (
    <button
      className={styles['workspace-btn']}
      onClick={onClick}
      ref={setButtonNode}
      data-collapsed={navState.collapsed}
    >
      <div data-collapsed={navState.collapsed} className={styles['workspace-image-container']}>
        <WorkspaceImage url={workspaceImage} placeholderText={workspaceName.charAt(0)?.toUpperCase()} />
      </div>
      <AnimatePresence initial={false}>
        {!navState.collapsed && (
          <motion.b
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: {
                delay: 0.2,
                duration: 0.2,
              },
            }}
            exit={{
              opacity: 0,
            }}
            className={styles['workspace-name']}
          >
            {workspaceName}
          </motion.b>
        )}
      </AnimatePresence>

      <FontAwesomeIcon icon={faAngleDown} className={styles['show-workspace-popover-icon']} />
    </button>
  );
};

export { WorkspaceButton, WorkspaceImage };
